import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import { Link } from "react-router-dom";
import { DollarSign, TrendingUp, Users, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /admin/billing — Owner-only revenue dashboard.
 * Pulls aggregate stats from payment_transactions:
 *   total/30d/7d revenue, active premium subscribers, breakdown by purchase
 *   type, last 20 transactions.
 */
export default function AdminBilling() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/billing/overview`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load billing data.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (isOwner) load();
  }, [isOwner]);

  if (!isOwner) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Header />
        <div className="pt-32 text-center text-neutral-400">Owner only.</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-billing-page">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] font-bold">
              Admin
            </div>
            <h1 className="font-display text-3xl text-white leading-none mt-1">Billing</h1>
            <p className="text-xs text-neutral-500 mt-1">
              Live revenue + subscriber stats — auto-fulfilled via Stripe webhook.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/admin/promo-codes"
              className="border border-zinc-800 hover:border-[#D4AF37] hover:text-[#D4AF37] text-xs uppercase tracking-widest font-bold py-2.5 px-4 transition-colors"
            >
              Promo Codes →
            </Link>
            <button
              onClick={load}
              data-testid="admin-billing-refresh"
              className="border border-zinc-800 hover:border-[#D4AF37] hover:text-[#D4AF37] text-xs uppercase tracking-widest font-bold py-2.5 px-4 transition-colors inline-flex items-center gap-2"
            >
              <RefreshCw size={12} /> Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-neutral-500 py-12">Loading…</div>
        ) : !data ? null : (
          <>
            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <Kpi icon={<DollarSign size={14} />} label="Lifetime Revenue" value={`$${data.total_revenue_usd.toLocaleString(undefined, { minimumFractionDigits: 2 })}`} sub={`${data.total_paid_count} paid txn`} highlight />
              <Kpi icon={<TrendingUp size={14} />} label="Revenue · 30d" value={`$${data.revenue_30d_usd.toLocaleString(undefined, { minimumFractionDigits: 2 })}`} />
              <Kpi icon={<TrendingUp size={14} />} label="Revenue · 7d" value={`$${data.revenue_7d_usd.toLocaleString(undefined, { minimumFractionDigits: 2 })}`} />
              <Kpi icon={<Users size={14} />} label="Active Premium" value={data.active_premium_subscribers.toLocaleString()} sub="not incl. owner" />
            </div>

            {/* Breakdown */}
            <section className="mb-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-2">
                By Purchase Type
              </div>
              <div className="bg-[#0F0F0F] border border-zinc-900 divide-y divide-zinc-900">
                {data.breakdown_by_type.length === 0 ? (
                  <div className="px-4 py-6 text-center text-neutral-600 text-sm">No paid transactions yet.</div>
                ) : (
                  data.breakdown_by_type.map((b) => (
                    <div key={b.purchase_type} className="px-4 py-3 flex items-center justify-between">
                      <div className="font-bold text-white capitalize">{b.purchase_type.replace(/_/g, " ")}</div>
                      <div className="text-sm">
                        <span className="text-[#D4AF37] font-bold">${b.sum.toFixed(2)}</span>
                        <span className="text-neutral-500 ml-3">{b.count}×</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>

            {/* Recent transactions */}
            <section>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-2">
                Recent Transactions
              </div>
              <div className="overflow-x-auto bg-[#0F0F0F] border border-zinc-900">
                <table className="w-full text-sm" data-testid="admin-billing-recent">
                  <thead className="text-[10px] uppercase tracking-widest text-neutral-500">
                    <tr className="border-b border-zinc-900">
                      <th className="text-left px-3 py-2">Type</th>
                      <th className="text-right px-3 py-2">Amount</th>
                      <th className="text-left px-3 py-2">Status</th>
                      <th className="text-left px-3 py-2">When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_transactions.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-4 py-6 text-center text-neutral-600">
                          No transactions yet.
                        </td>
                      </tr>
                    ) : (
                      data.recent_transactions.map((tx) => (
                        <tr key={tx.session_id} className="border-b border-zinc-900 last:border-b-0">
                          <td className="px-3 py-2 capitalize">{(tx.purchase_type || "premium").replace(/_/g, " ")}</td>
                          <td className="px-3 py-2 text-right text-[#D4AF37] font-bold">${(tx.amount || 0).toFixed(2)}</td>
                          <td className="px-3 py-2">
                            <span className={
                              "text-[10px] uppercase tracking-widest font-bold " +
                              (tx.fulfilled ? "text-emerald-400" : tx.payment_status === "paid" ? "text-yellow-400" : "text-neutral-500")
                            }>
                              {tx.fulfilled ? "Fulfilled" : tx.payment_status || "Pending"}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-xs text-neutral-500">
                            {tx.created_at ? new Date(tx.created_at).toLocaleString() : ""}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}

function Kpi({ icon, label, value, sub, highlight }) {
  return (
    <div
      className={
        "p-4 border-2 bg-[#0F0F0F] " +
        (highlight ? "border-[#D4AF37]" : "border-zinc-900")
      }
    >
      <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-neutral-500 flex items-center gap-1.5">
        {icon} {label}
      </div>
      <div className={"font-display text-2xl mt-1 leading-none " + (highlight ? "text-[#D4AF37]" : "text-white")}>{value}</div>
      {sub && <div className="text-[10px] uppercase tracking-widest text-neutral-600 mt-1">{sub}</div>}
    </div>
  );
}
